import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MDButton from "components/MDButton";
import api from "services/api";

function LineItemMatchCard({ item, companyId, defaultWarehouseCode, defaultGroup, onResolved }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const warehouseCode = item.warehouse_code ?? defaultWarehouseCode ?? "";
  const [newItem, setNewItem] = useState({
    item_code: item.sku || `MAN-${item.line_item_id}`,
    name: item.description || "",
    warehouse_code: warehouseCode,
    group: defaultGroup || "Prekės",
  });

  useEffect(() => {
    if (!searchOpen || !companyId || !warehouseCode) return undefined;
    let active = true;
    const controller = new AbortController();
    setLoadingOptions(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        // Each row carries its own supplier's warehouse, including mixed-supplier
        // exports. Never broaden an empty warehouse to the whole catalog.
        const response = await api.get("/api/warehouse/items/", {
          params: {
            company_id: companyId,
            search: query,
            warehouse_code: warehouseCode,
            page_size: 30,
          },
          signal: controller.signal,
        });
        if (active) setOptions(response.data?.items || []);
      } catch (requestError) {
        if (active) {
          setOptions([]);
          setError(requestError.response?.data?.error || "Nepavyko gauti sandėlio prekių.");
        }
      } finally {
        if (active) setLoadingOptions(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [companyId, query, searchOpen, warehouseCode]);

  const assign = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    try {
      await api.post("/api/match-review/assign/", {
        line_item_id: item.line_item_id,
        warehouse_item_id: selected.id,
      });
      onResolved(item.line_item_id);
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Nepavyko išsaugoti priskyrimo.");
    } finally {
      setSaving(false);
    }
  };

  const createAndAssign = async () => {
    setSaving(true);
    setError("");
    try {
      await api.post("/api/match-review/create-and-assign/", {
        line_item_id: item.line_item_id,
        ...newItem,
      });
      onResolved(item.line_item_id);
    } catch (requestError) {
      setError(requestError.response?.data?.error || "Nepavyko sukurti sandėlio prekės.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: "divider" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {item.document_title}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {item.description || "Be pavadinimo"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.sku ? `Tiekėjo kodas: ${item.sku} · ` : ""}Kiekis: {item.quantity || "—"}{" "}
            {item.unit_of_measure}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Eilutė #{item.line_item_id}
        </Typography>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
      <Autocomplete
        sx={{ mt: 1.5 }}
        value={selected}
        options={options}
        loading={loadingOptions}
        disabled={!warehouseCode}
        open={searchOpen}
        onOpen={() => setSearchOpen(true)}
        onClose={() => setSearchOpen(false)}
        onChange={(_, value) => setSelected(value)}
        onInputChange={(_, value, reason) => {
          if (reason === "input" || reason === "clear") setQuery(value);
        }}
        filterOptions={(results) => results}
        loadingText="Ieškoma sandėlio prekių…"
        noOptionsText={`Sandėlyje „${warehouseCode}“ pagal užklausą prekių nerasta`}
        getOptionLabel={(option) =>
          `${option.item_code} — ${option.name}${
            option.warehouse_code ? ` (${option.warehouse_code})` : ""
          }`
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Rasti ir pasirinkti sandėlio prekę"
            placeholder="Kodas arba pavadinimas"
            helperText={
              warehouseCode
                ? `Ieškoma tiekėjui nustatytame sandėlyje: ${warehouseCode}`
                : "Tiekėjui nenustatytas sandėlis. Patikrinkite tiekėjo nustatymus."
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingOptions ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mt: 1 }}>
        <MDButton color="info" onClick={assign} disabled={!selected || saving}>
          Priskirti šiai eilutei
        </MDButton>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={() => setShowCreate((value) => !value)}
          disabled={saving}
        >
          Sukurti naują prekę šiai eilutei
        </MDButton>
      </Box>
      {showCreate && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
            mt: 2,
          }}
        >
          <TextField
            label="Prekės kodas"
            value={newItem.item_code}
            onChange={(event) => setNewItem({ ...newItem, item_code: event.target.value })}
            required
          />
          <TextField
            label="Pavadinimas"
            value={newItem.name}
            onChange={(event) => setNewItem({ ...newItem, name: event.target.value })}
            required
          />
          <TextField
            label="Sandėlio kodas"
            value={newItem.warehouse_code}
            onChange={(event) => setNewItem({ ...newItem, warehouse_code: event.target.value })}
            required
          />
          <TextField
            label="Grupė"
            value={newItem.group}
            onChange={(event) => setNewItem({ ...newItem, group: event.target.value })}
            required
          />
          <Box>
            <MDButton color="success" onClick={createAndAssign} disabled={saving}>
              Sukurti ir priskirti šiai eilutei
            </MDButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function UnmatchedLineItemsDialog({
  open,
  loading,
  items,
  companyId,
  defaultWarehouseCode,
  defaultGroup,
  onClose,
  onExport,
  onGenerateAndExport,
}) {
  const [remaining, setRemaining] = useState([]);
  const [generatingRandomCodes, setGeneratingRandomCodes] = useState(false);
  const [generationError, setGenerationError] = useState("");
  useEffect(() => {
    if (open && !loading) setRemaining(items);
  }, [open, loading, items]);
  const resolve = (lineItemId) =>
    setRemaining((previous) => previous.filter((item) => item.line_item_id !== lineItemId));
  const finish = () => {
    onClose();
    onExport();
  };
  const generateAndExport = async () => {
    setGeneratingRandomCodes(true);
    setGenerationError("");
    try {
      await onGenerateAndExport(remaining.map((item) => item.line_item_id));
    } catch (error) {
      setGenerationError(
        error.response?.data?.error || "Nepavyko sugeneruoti ir įtraukti sandėlio prekių."
      );
    } finally {
      setGeneratingRandomCodes(false);
    }
  };
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {loading
          ? "Tikrinami sandėlio atitikmenys…"
          : `Nerasti sandėlio atitikmenys (${remaining.length})`}
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "70vh" }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={28} />
            <Typography>
              Ieškoma atitikmenų sandėlyje. Dideliems dokumentams tai gali užtrukti.
            </Typography>
          </Box>
        ) : (
          <>
            {generationError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {generationError}
              </Alert>
            )}
            {remaining.map((item) => (
              <LineItemMatchCard
                key={item.line_item_id}
                item={item}
                companyId={companyId}
                defaultWarehouseCode={defaultWarehouseCode}
                defaultGroup={defaultGroup}
                onResolved={resolve}
              />
            ))}
            {remaining.length === 0 && (
              <Alert severity="success">Visoms eilutėms priskirtos sandėlio prekės.</Alert>
            )}
          </>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions>
          <Typography variant="caption" sx={{ mr: "auto", pl: 1 }}>
            Priskirta šiame lange: {Math.max(0, items.length - remaining.length)}
          </Typography>
          <MDButton variant="text" color="dark" onClick={onClose}>
            Atšaukti
          </MDButton>
          {remaining.length > 0 ? (
            <>
              <MDButton
                color="success"
                onClick={generateAndExport}
                disabled={generatingRandomCodes}
              >
                {generatingRandomCodes
                  ? "Generuojami kodai…"
                  : "Sugeneruoti kodus, įtraukti į sandėlį ir eksportuoti"}
              </MDButton>
              <MDButton color="warning" onClick={finish} disabled={generatingRandomCodes}>
                Eksportuoti su likusiomis ({remaining.length})
              </MDButton>
            </>
          ) : (
            <MDButton color="success" onClick={finish}>
              Tęsti eksportą
            </MDButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

LineItemMatchCard.propTypes = {
  item: PropTypes.object.isRequired,
  companyId: PropTypes.number,
  defaultWarehouseCode: PropTypes.string,
  defaultGroup: PropTypes.string,
  onResolved: PropTypes.func.isRequired,
};
UnmatchedLineItemsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  items: PropTypes.array.isRequired,
  companyId: PropTypes.number,
  defaultWarehouseCode: PropTypes.string,
  defaultGroup: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onGenerateAndExport: PropTypes.func.isRequired,
};
export default UnmatchedLineItemsDialog;
